/**
 * Header Component - JavaScript
 * GSAP-powered animations and interactions
 * ============================================
 */

(function() {
  'use strict';

  document.addEventListener('DOMContentLoaded', initHeader);

  document.addEventListener('shopify:section:unload', function(e) {
    if (e.target && e.target.querySelector('[data-header]') && window.UntamedStickyOffset) {
      window.UntamedStickyOffset.unregister('header');
    }
  });

  function initHeader() {
    const header = document.querySelector('[data-header]');
    const drawer = document.querySelector('[data-drawer]');

    if (!header) return;

    const section = header.closest('.shopify-section');
    const hasStickyClass = header.classList.contains('header--sticky-enabled');

    if (hasStickyClass && section && window.UntamedStickyOffset) {
      let topProxy = { value: 0 };
      let topTween = null;
      const setHeaderTopOffset = (animated) => {
        const offsetBefore = window.UntamedStickyOffset.getOffsetBefore('header');
        const newTop = Math.max(offsetBefore, 0);
        const currentTop = parseFloat(section.style.getPropertyValue('--header-sticky-top')) || 0;
        if (currentTop === newTop) return;
        if (topTween) topTween.kill();
        if (animated !== false && typeof gsap !== 'undefined') {
          topProxy.value = currentTop;
          topTween = gsap.to(topProxy, {
            value: newTop,
            duration: 0.3,
            ease: 'power2.inOut',
            onUpdate: () => {
              section.style.setProperty('--header-sticky-top', topProxy.value + 'px');
            },
            onComplete: () => {
              section.style.setProperty('--header-sticky-top', newTop + 'px');
            }
          });
        } else {
          section.style.setProperty('--header-sticky-top', newTop + 'px');
        }
      };

      const registerHeaderOffset = (animated) => {
        const sectionHeight = section.getBoundingClientRect().height;
        window.UntamedStickyOffset.register('header', sectionHeight, section);
        setHeaderTopOffset(animated);
      };

      registerHeaderOffset(false);
      window.addEventListener('untamed:offset-changed', (e) => setHeaderTopOffset(e.detail && e.detail.instant ? false : true));
      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(registerHeaderOffset).observe(section);
      } else {
        window.addEventListener('resize', registerHeaderOffset);
      }
    } else if (window.UntamedStickyOffset) {
      window.UntamedStickyOffset.unregister('header');
      if (section) {
        section.style.removeProperty('--header-sticky-top');
      }
    }

    // Initialize all header features
    initSearchButton(header);
    initDrawer(header, drawer);
    initCartAnimation();
    initLocalizationDrawer();
  }

  function initSearchButton(_header) {
    // Delegate from the document so triggers in sibling header-group sections
    // (e.g. the standalone `search-bar` section) also open the search drawer,
    // not just buttons rendered inside the <header> element itself.
    if (document._untamedSearchBarBtnBound) return;
    document._untamedSearchBarBtnBound = true;
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-search-bar-btn]');
      if (!trigger) return;
      window.dispatchEvent(new CustomEvent('search:open-drawer'));
    });
  }

  /**
   * Menu Drawer - Two Column Desktop, Accordion Mobile
   */
  function initDrawer(header, drawer) {
    if (!drawer) return;

    const toggleBtn = header.querySelector('[data-drawer-toggle]');
    const closeBtn = drawer.querySelector('[data-drawer-close]');
    const overlay = drawer.querySelector('[data-drawer-overlay]');
    const accordionBtns = drawer.querySelectorAll('[data-drawer-accordion]');
    const submenuTriggers = drawer.querySelectorAll('[data-drawer-submenu-trigger]');
    const submenuPanels = drawer.querySelectorAll('[data-submenu-panel]');
    const submenuContainer = drawer.querySelector('[data-drawer-submenu]');

    const accordionBehavior = drawer.getAttribute('data-md-accordion') || 'single';
    const defaultOpenIndex = parseInt(drawer.getAttribute('data-md-default-open'), 10) || 0;
    const closeOnScrim = drawer.getAttribute('data-md-close-on-scrim') !== 'false';
    const closeOnSwipe = drawer.getAttribute('data-md-close-on-swipe') !== 'false';
    const slideFrom = drawer.getAttribute('data-md-slide') || 'left';
    const analyticsOn = drawer.getAttribute('data-md-analytics') === 'true';

    function emitAnalytics(name, detail) {
      if (!analyticsOn) return;
      window.dispatchEvent(new CustomEvent('untamed:menu', { detail: Object.assign({ type: name }, detail || {}) }));
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'menuInteraction', menuAction: name });
    }

    function applyDefaultOpen() {
      if (accordionBehavior === 'all_open') {
        accordionBtns.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
        return;
      }
      if (defaultOpenIndex > 0) {
        const target = drawer.querySelector('[data-drawer-accordion][data-menu-index="' + (defaultOpenIndex - 1) + '"]');
        if (target) target.setAttribute('aria-expanded', 'true');
      }
    }

    let isDesktop = window.innerWidth >= 1024;

    let defaultOpenApplied = false;

    function openDrawer() {
      drawer.setAttribute('aria-hidden', 'false');
      toggleBtn.setAttribute('aria-expanded', 'true');
      document.body.classList.add('drawer-open');
      emitAnalytics('open');

      if (!defaultOpenApplied) {
        applyDefaultOpen();
        defaultOpenApplied = true;
      }

      // Focus trap - focus on close button
      setTimeout(() => closeBtn.focus(), 100);

      // GSAP animation for drawer items
      const prefersReduced = drawer.getAttribute('data-md-reduce-motion') !== 'false'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (typeof gsap !== 'undefined' && !prefersReduced) {
        const items = drawer.querySelectorAll('[data-drawer-item]');
        const footer = drawer.querySelector('[data-drawer-footer]');
        
        gsap.fromTo(items,
          { opacity: 0, x: -30 },
          { 
            opacity: 1, 
            x: 0, 
            duration: 0.4, 
            stagger: 0.05,
            ease: 'power3.out',
            delay: 0.1
          }
        );
        
        if (footer) {
          gsap.fromTo(footer,
            { opacity: 0, y: 20 },
            { 
              opacity: 1, 
              y: 0, 
              duration: 0.3, 
              ease: 'power2.out',
              delay: 0.3
            }
          );
        }
      }

    }

    function closeDrawer() {
      drawer.setAttribute('aria-hidden', 'true');
      toggleBtn.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('drawer-open');
      emitAnalytics('close');

      // Reset submenu state
      hideAllSubmenus();
      
      // Return focus to toggle button
      toggleBtn.focus();
    }

    function showSubmenu(index) {
      // Hide all panels first
      submenuPanels.forEach(panel => {
        panel.classList.remove('is-active');
      });
      
      // Remove selected state from all triggers
      submenuTriggers.forEach(trigger => {
        trigger.classList.remove('is-selected');
        trigger.setAttribute('aria-expanded', 'false');
      });

      // Show the selected panel
      const targetPanel = drawer.querySelector(`[data-submenu-panel="${index}"]`);
      if (targetPanel) {
        targetPanel.classList.add('is-active');
        
        // Find and mark the trigger as selected
        const trigger = drawer.querySelector(`[data-drawer-submenu-trigger][data-menu-index="${index}"]`);
        if (trigger) {
          trigger.classList.add('is-selected');
          trigger.setAttribute('aria-expanded', 'true');
        }

        // GSAP animation for submenu items
        if (typeof gsap !== 'undefined') {
          const links = targetPanel.querySelectorAll('.drawer__submenu-link');
          gsap.fromTo(links,
            { opacity: 0, x: 20 },
            { 
              opacity: 1, 
              x: 0, 
              duration: 0.3, 
              stagger: 0.03,
              ease: 'power2.out'
            }
          );
        }
      }
    }

    function hideAllSubmenus() {
      submenuPanels.forEach(panel => {
        panel.classList.remove('is-active');
      });
      submenuTriggers.forEach(trigger => {
        trigger.classList.remove('is-selected');
        trigger.setAttribute('aria-expanded', 'false');
      });
    }

    // Event listeners
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        const isOpen = drawer.getAttribute('aria-hidden') === 'false';
        if (isOpen) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', closeDrawer);
    }


    if (overlay && closeOnScrim) {
      overlay.addEventListener('click', closeDrawer);
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
        closeDrawer();
      }
    });

    // Mobile: Accordion for nested nav (2nd level)
    accordionBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';

        // In "single" mode only one section stays open at a time
        if (accordionBehavior === 'single') {
          accordionBtns.forEach(otherBtn => {
            if (otherBtn !== btn) {
              otherBtn.setAttribute('aria-expanded', 'false');
            }
          });
        }

        btn.setAttribute('aria-expanded', !isExpanded);
        emitAnalytics('nav_click', { label: btn.textContent.trim() });
      });
    });

    // Mobile: Accordion for 3rd level nested nav
    const subnavAccordionBtns = drawer.querySelectorAll('[data-drawer-subnav-accordion]');
    subnavAccordionBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';
        
        // Close other 3rd level accordions within the same parent
        const parentSubnav = btn.closest('.drawer__subnav');
        if (parentSubnav) {
          const siblingBtns = parentSubnav.querySelectorAll('[data-drawer-subnav-accordion]');
          siblingBtns.forEach(otherBtn => {
            if (otherBtn !== btn) {
              otherBtn.setAttribute('aria-expanded', 'false');
            }
          });
        }
        
        btn.setAttribute('aria-expanded', !isExpanded);
      });
    });

    // Desktop: Submenu triggers (click only - no hover)
    submenuTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const menuIndex = trigger.dataset.menuIndex;
        const isCurrentlySelected = trigger.classList.contains('is-selected');
        
        if (isCurrentlySelected) {
          // Toggle off - hide submenu
          hideAllSubmenus();
        } else {
          // Show this submenu
          showSubmenu(menuIndex);
        }
      });
    });

    // Mobile: swipe toward the drawer's anchored edge to dismiss
    if (closeOnSwipe) {
      const content = drawer.querySelector('.drawer__content');
      if (content) {
        let startX = 0;
        let startY = 0;
        let tracking = false;
        content.addEventListener('touchstart', (e) => {
          if (window.innerWidth >= 1024) return;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          tracking = true;
        }, { passive: true });
        content.addEventListener('touchmove', (e) => {
          if (!tracking) return;
          const dx = e.touches[0].clientX - startX;
          const dy = e.touches[0].clientY - startY;
          if (Math.abs(dx) < Math.abs(dy)) { tracking = false; return; }
          const dismiss = slideFrom === 'right' ? dx > 70 : dx < -70;
          if (dismiss) {
            tracking = false;
            closeDrawer();
          }
        }, { passive: true });
        content.addEventListener('touchend', () => { tracking = false; });
      }
    }

    // Update isDesktop on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        isDesktop = window.innerWidth >= 1024;
        
        // Reset submenu state when switching between mobile/desktop
        if (drawer.getAttribute('aria-hidden') === 'false') {
          hideAllSubmenus();
        }
      }, 100);
    });
  }

  /**
   * Cart Count Animation
   */
  function initCartAnimation() {
    const cartCount = document.querySelector('[data-cart-count]');
    if (!cartCount) return;

    // Observe cart count changes for animation
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData' || mutation.type === 'childList') {
          // Add bump animation
          cartCount.classList.add('is-bumping');
          
          // Update visibility
          const count = parseInt(cartCount.textContent) || 0;
          if (count > 0) {
            cartCount.classList.add('is-visible');
          } else {
            cartCount.classList.remove('is-visible');
          }
          
          // Remove animation class
          setTimeout(() => {
            cartCount.classList.remove('is-bumping');
          }, 300);
        }
      });
    });

    observer.observe(cartCount, {
      characterData: true,
      childList: true,
      subtree: true
    });
  }


  /**
   * Localization Drawer - Region & Currency Selector
   */
  function initLocalizationDrawer() {
    const drawer = document.querySelector('[data-localization-drawer]');
    if (!drawer) return;

    const toggleBtns = document.querySelectorAll('[data-localization-toggle]');
    const closeBtns = drawer.querySelectorAll('[data-localization-close]');

    function openLocDrawer() {
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('localization-drawer-open');
      const closeBtn = drawer.querySelector('[data-localization-close]:not(.localization-drawer__overlay)');
      if (closeBtn) setTimeout(() => closeBtn.focus(), 100);
    }

    function closeLocDrawer() {
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('localization-drawer-open');
    }

    toggleBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const isOpen = drawer.getAttribute('aria-hidden') === 'false';
        if (isOpen) closeLocDrawer(); else openLocDrawer();
      });
    });

    closeBtns.forEach(btn => {
      btn.addEventListener('click', closeLocDrawer);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
        closeLocDrawer();
      }
    });
  }

})();

